import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Button, Typography, Alert, CircularProgress
} from '@mui/material';
import { School } from '@mui/icons-material';
import { useAuth } from '../AuthContext';
import { signInWithGoogle } from '../firebase';

export default function LoginPage() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (isAuthenticated) {
    navigate('/');
    return null;
  }

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const { idToken, user } = await signInWithGoogle();
      login(idToken, {
        name: user.displayName,
        email: user.email,
        photo: user.photoURL,
        uid: user.uid,
      });
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        setLoading(false);
        return;
      }
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 2
    }}>
      <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 4 }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
            background: 'linear-gradient(135deg, #6366f1, #ec4899)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(99,102,241,0.3)'
          }}>
            <School sx={{ color: 'white', fontSize: 36 }} />
          </Box>
          <Typography variant="h4" fontWeight={800} sx={{ mb: 0.5 }}>Class Monitor</Typography>
          <Typography color="text.secondary" variant="body1" sx={{ mb: 3 }}>
            Track attendance & payments effortlessly
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}

          <Button
            fullWidth variant="outlined" size="large"
            onClick={handleGoogleLogin} disabled={loading}
            sx={{
              py: 1.5, borderRadius: 3, borderColor: '#dadce0', color: 'text.primary',
              fontSize: '1rem', fontWeight: 600,
              '&:hover': { borderColor: '#bdc1c6', bgcolor: '#f8f9fa' },
            }}
            startIcon={loading ? <CircularProgress size={20} /> : (
              <svg width="20" height="20" viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
            )}
          >
            {loading ? 'Signing in...' : 'Continue with Google'}
          </Button>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, lineHeight: 1.5 }}>
            Only authorized accounts can access this app.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
